import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import { useCalendarEventStore, useAuthStore } from '../../stores/root_store';
import { infoApi } from '../../api/info_api';
import type { InfoRecord } from '../../api/info_api';
import dayjs from 'dayjs';

interface ClientCalendarCardProps {
  requestKey: string;
}

export const ClientCalendarCard = observer(function ClientCalendarCard({
  requestKey,
}: ClientCalendarCardProps) {
  const location = useLocation();
  const calendarEventStore = useCalendarEventStore();
  const authStore = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [hasCalendar, setHasCalendar] = useState(false);
  const [createdEvents, setCreatedEvents] = useState<InfoRecord[]>([]);

  const userUuid = authStore.user?.uuid;

  useEffect(() => {
    const checkCalendar = async () => {
      if (!requestKey || !userUuid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await calendarEventStore.checkHasCalendar(
          'request',
          requestKey
        );
        setHasCalendar(result);
      } catch {
        setHasCalendar(false);
      } finally {
        setLoading(false);
      }
    };

    checkCalendar();
  }, [requestKey, userUuid, calendarEventStore]);

  // Fetch created calendar events for this request
  useEffect(() => {
    const fetchCreatedEvents = async () => {
      if (!requestKey) return;
      try {
        const response = await infoApi.listByParent({
          parent_type: 'request',
          parent_uuid: requestKey,
          associated_type: 'calendar:event-create',
        });
        setCreatedEvents(response.items || []);
      } catch (err) {
        console.error('Error fetching created events:', err);
      }
    };
    fetchCreatedEvents();
  }, [requestKey, location.pathname]);

  // Parse event data from Info record
  const parseEventData = (info: InfoRecord) => {
    try {
      const data = JSON.parse(info.data);
      return {
        summary: data.summary || 'Untitled Event',
        start: data.start?.dateTime || data.start?.date,
      };
    } catch {
      return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarMonthIcon color="primary" />
          <Typography variant="h6">Calendar</Typography>
          {loading && <CircularProgress size={16} />}
        </Box>

        {!loading && !hasCalendar && (
          <Typography variant="body2" color="text.secondary">
            Connect your calendar to let us schedule events for you.{' '}
            <Link component={RouterLink} to="/settings/calendar">
              Connect Calendar
            </Link>
          </Typography>
        )}

        {!loading && hasCalendar && (
          <>
            {createdEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No events have been created for this request yet.
              </Typography>
            ) : (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Events Created
                </Typography>
                <List dense disablePadding>
                  {createdEvents.map((info) => {
                    const event = parseEventData(info);
                    if (!event) return null;
                    return (
                      <ListItem key={info.uuid} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <EventIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={event.summary}
                          secondary={event.start ? dayjs(event.start).format('MMM D, YYYY h:mm A') : ''}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
});
